import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Chip from "@material-ui/core/Chip";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PrintIcon from "@material-ui/icons/Print";
import EditIcon from "@material-ui/icons/Edit";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { hasPermission } from "../../helpers/hasPermission";

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];
const DOW_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// Paleta fixa - cor atribuida por POSICAO no roster de usuarios (ordenado
// por id), nao por hash do id. Hash (userId % N) colide sempre que dois
// ids diferem por um multiplo de N (ex: Matheus id=8, Lucas Sales id=18
// colidiam com N=10). Por posicao, so colide se houver mais usuarios que
// cores na paleta - por isso a paleta tem 16 cores.
//
// Pedido do usuario 2026-10-13: "essas cores estao muito forte, segue as
// da planilha" - tons pastel/suaves (mesma familia da planilha original:
// pessego FBE5D6, amarelo FFF2CC, verde C5E0B4, azul DAE3F3), nao mais as
// cores saturadas de antes. Com fundo claro o texto precisa ser escuro
// (ver colorForUserIsLight logic abaixo), nao branco.
const USER_COLORS = [
  "#FBE5D6", "#DAE3F3", "#C5E0B4", "#FFF2CC",
  "#E2D4F0", "#F4CCCC", "#D0E6E6", "#F7D9B4",
  "#D9EAD3", "#CFE2F3", "#EAD1DC", "#FFE5B4",
  "#D5A6BD", "#B6D7A8", "#A4C2F4", "#D9D2E9"
];
const buildUserColorMap = (users) => {
  const map = new Map();
  const sorted = [...users].sort((a, b) => a.id - b.id);
  sorted.forEach((u, i) => map.set(u.id, USER_COLORS[i % USER_COLORS.length]));
  return map;
};

// Pedido do usuario 2026-10-13: redesign visual do calendario de escala
// (aba Escala), seguindo mockup fornecido ("Escala — Outubro 2026"). Cada
// pessoa recebe um conjunto {bg, fg, dot} em vez de uma cor so' - bg pro
// fundo do card/chip, fg pro texto escuro saturado, dot pro destaque
// (bolinha/avatar). So' usado nessa visualizacao (grade da aba Escala +
// legenda); a impressao e o relatorio continuam com a paleta antiga
// (USER_COLORS/colorForUser), que nao foi pedido pra mudar.
const PERSON_COLOR_SETS = [
  { bg: "#FBE3EA", fg: "#8E1F42", dot: "#C93B66" }, // rosa
  { bg: "#DCEFDF", fg: "#1C5E30", dot: "#2F8A4B" }, // verde
  { bg: "#E8E2FA", fg: "#45309A", dot: "#6E4FD6" }, // roxo
  { bg: "#FDEFC8", fg: "#7A4F00", dot: "#C98B00" }, // amarelo
  { bg: "#FBE4D2", fg: "#7A3B00", dot: "#C9701F" }, // laranja
  { bg: "#DCEAFB", fg: "#1A3E78", dot: "#2F6FC9" }, // azul
  { bg: "#D8F0EC", fg: "#0F5C53", dot: "#1E8F80" }, // turquesa
  { bg: "#EAE3EC", fg: "#4A3554", dot: "#7A5C8C" }, // lilás
  { bg: "#F7DFDA", fg: "#7A2A1E", dot: "#C14E33" }, // terracota
  { bg: "#ECEFD4", fg: "#4F5A1D", dot: "#85962E" }, // oliva
];

// "Lucas Sales" -> "LS", "Matheus" -> "MA".
const getInitials = (name) => {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const pad = (n) => String(n).padStart(2, "0");
const formatBR = (isoDate) => {
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}`;
};
const formatCurrency = (v) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto", overflowX: "hidden",
    ...theme.scrollbarStyles
  },
  monthNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  legend: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  legendDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
  },
  calGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: theme.spacing(0.75),
    minWidth: 700,
  },
  calDow: {
    fontSize: 10,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    textAlign: "center",
    paddingBottom: 4,
  },
  calCell: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    minHeight: 56,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
    transition: "box-shadow .12s ease",
    "&:hover": {
      boxShadow: "0 0 0 2px " + theme.palette.primary.main + "55",
    },
  },
  calCellSunday: {
    background: theme.mode === "light" ? "#f7f7f9" : "#20222b",
    color: theme.palette.text.disabled,
    cursor: "default",
  },
  calCellHoliday: {
    border: "2.5px solid #d6332b",
  },
  calDaynum: {
    fontSize: 11,
    fontWeight: 800,
  },
  calHolidayBadge: {
    position: "absolute",
    top: 4,
    right: 4,
    fontSize: 6.5,
    fontWeight: 800,
    letterSpacing: ".03em",
    color: "#fff",
    background: "#d6332b",
    padding: "1px 4px",
    borderRadius: 4,
  },
  calHolidayLabel: {
    fontSize: 8,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#d6332b",
    background: "#fff",
    borderRadius: 4,
    padding: "1px 4px",
    alignSelf: "flex-start",
  },
  calTechTag: {
    fontSize: 11,
    fontWeight: 800,
    alignSelf: "flex-start",
  },
  reportSection: {
    marginBottom: theme.spacing(2),
  },
  reportTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 11,
  },
  reportHeadRow: {
    "& th": {
      textAlign: "left",
      fontSize: 9,
      textTransform: "uppercase",
      letterSpacing: ".02em",
      color: "#fff",
      padding: "4px 10px",
    },
  },
  reportTd: {
    padding: "3px 10px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  reportValTd: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontWeight: 700,
    color: "#1f8a5f",
  },
  rateCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    padding: theme.spacing(1.75),
  },
  printOnly: {
    display: "none",
  },
  // Pedido do usuario 2026-10-13: "cabe ate 3 meses na mesma folha de
  // sulfite", corrigido: "em uma folha A4 cabe um calendario embaixo do
  // outro e nao lado a lado" - A4 retrato e' estreito demais pra 3
  // colunas lado a lado (cada mini-calendario de 7 colunas ficava
  // espremido); empilhado (1 coluna, 3 linhas) usa a largura toda da
  // folha e cabe 3 meses de altura.
  printMonthBlock: {
    pageBreakInside: "avoid",
    breakInside: "avoid",
  },
  "@media print": {
    noPrint: {
      display: "none !important",
    },
    printOnly: {
      display: "flex !important",
      flexDirection: "column",
      gap: "8px",
    },
  },

  // ---- Redesign do calendario de escala (aba Escala), pedido do
  // usuario 2026-10-13 a partir de um mockup especifico. Visual fixo
  // (nao segue dark mode do app - o mockup define cores proprias). ----
  scheduleWrap: {
    background: "#F4F3EF",
    borderRadius: 20,
    padding: 48,
    display: "flex",
    flexDirection: "column",
    gap: 28,
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    color: "#1C1C1E",
    [theme.breakpoints.down("sm")]: {
      padding: 20,
      gap: 20,
    },
    // Pedido do usuario 2026-10-13: "cabe ate 3 meses na mesma folha",
    // empilhados (nao lado a lado) - versao compacta pra 3 blocos
    // empilhados caberem na altura de uma folha A4 (mesmas cores/formas
    // do layout de tela, so' menor).
    "@media print": {
      padding: 10,
      gap: 8,
      borderRadius: 10,
    },
  },
  scheduleHeader: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 24,
    "@media print": {
      gap: 8,
      alignItems: "flex-start",
      flexDirection: "column",
    },
  },
  scheduleEyebrow: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#6B6B70",
    margin: 0,
    "@media print": {
      fontSize: 8,
    },
  },
  scheduleTitle: {
    fontSize: 52,
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: "-0.02em",
    margin: "6px 0 0",
    [theme.breakpoints.down("xs")]: {
      fontSize: 36,
    },
    "@media print": {
      fontSize: 20,
      margin: "2px 0 0",
    },
  },
  scheduleTitleYear: {
    color: "#9A9AA0",
    fontWeight: 600,
  },
  scheduleLegend: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    "@media print": {
      gap: 4,
    },
  },
  scheduleChip: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    borderRadius: 999,
    padding: "8px 14px 8px 8px",
    "@media print": {
      gap: 4,
      padding: "2px 8px 2px 2px",
    },
  },
  scheduleChipAvatar: {
    width: 30,
    height: 30,
    minWidth: 30,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 800,
    color: "#fff",
    "@media print": {
      width: 16,
      height: 16,
      minWidth: 16,
      fontSize: 7,
    },
  },
  scheduleChipName: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.25,
    "@media print": {
      fontSize: 7.5,
    },
  },
  scheduleChipCount: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.25,
    "@media print": {
      fontSize: 6.5,
    },
  },
  scheduleDowRow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: 10,
    "@media print": {
      gap: 3,
    },
  },
  scheduleDowLabel: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#6B6B70",
    paddingLeft: 2,
    "@media print": {
      fontSize: 6.5,
      paddingLeft: 0,
      textAlign: "center",
    },
  },
  scheduleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gridAutoRows: "1fr",
    gap: 10,
    "@media print": {
      gap: 3,
    },
  },
  scheduleCellEmpty: {
    background: "transparent",
  },
  scheduleCell: {
    position: "relative",
    borderRadius: 14,
    padding: 14,
    minHeight: 108,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    cursor: "pointer",
    "@media print": {
      borderRadius: 4,
      padding: 3,
      minHeight: 28,
      gap: 1,
    },
  },
  scheduleCellOff: {
    background: "#FFFFFF",
    boxShadow: "inset 0 0 0 1px #E3E2DD",
  },
  scheduleDayNum: {
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1,
    "@media print": {
      fontSize: 9,
    },
  },
  scheduleBottomRow: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    gap: 8,
    "@media print": {
      gap: 3,
    },
  },
  scheduleDot: {
    width: 8,
    height: 8,
    minWidth: 8,
    borderRadius: "50%",
    "@media print": {
      width: 4,
      height: 4,
      minWidth: 4,
    },
  },
  schedulePersonName: {
    fontSize: 15,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "@media print": {
      fontSize: 6.5,
    },
  },
  scheduleOffLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: "#9A9AA0",
    "@media print": {
      fontSize: 6,
    },
  },
  scheduleHolidayBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#fff",
    background: "#C0262D",
    padding: "4px 7px",
    borderRadius: 6,
    "@media print": {
      top: 2,
      right: 2,
      fontSize: 5,
      padding: "1px 3px",
      borderRadius: 3,
    },
  },
  scheduleHolidayName: {
    fontSize: 11,
    fontWeight: 700,
    color: "#A11F25",
    lineHeight: 1.25,
    "@media print": {
      fontSize: 5,
      lineHeight: 1.1,
    },
  },
}));

const OnCall = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const today = new Date();

  // Pedido do usuario 2026-10-23: um perfil que so' pode VER o
  // calendario de quem esta de sobreaviso, sem gerar escala, reatribuir
  // dia nem ver Relatorio/Configuracoes (custos, feriados,
  // indisponibilidades - mais sensivel que so' "quem esta de plantao").
  // hasPermission ja' retorna true pra admin em qualquer chave, entao
  // quem tem a permissao cheia (menu:on-call) ou e' admin cai em
  // hasFullAccess=true e isViewOnly fica false (acesso completo vence).
  const hasFullAccess = hasPermission(user, "menu:on-call");
  const isViewOnly = !hasFullAccess && hasPermission(user, "oncall:calendar-view");

  const [tab, setTab] = useState(0);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [days, setDays] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingCal, setLoadingCal] = useState(false);

  const [assignAnchor, setAssignAnchor] = useState(null);
  const [assignDate, setAssignDate] = useState(null);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [genStart, setGenStart] = useState("");
  const [genEnd, setGenEnd] = useState("");
  const [genUserIds, setGenUserIds] = useState([]);
  const [generating, setGenerating] = useState(false);

  const [printAnchor, setPrintAnchor] = useState(null);
  const [printMonths, setPrintMonths] = useState([]);
  const [printData, setPrintData] = useState([]);
  const [preparingPrint, setPreparingPrint] = useState(false);

  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const [rates, setRates] = useState(null);
  const [savingRates, setSavingRates] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [newHolidayDate, setNewHolidayDate] = useState("");
  const [newHolidayName, setNewHolidayName] = useState("");
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [newUnavailUserId, setNewUnavailUserId] = useState("");
  const [newUnavailStart, setNewUnavailStart] = useState("");
  const [newUnavailEnd, setNewUnavailEnd] = useState("");

  const printRef = useRef(null);

  const usersById = useMemo(() => {
    const map = new Map();
    users.forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  const userColorMap = useMemo(() => buildUserColorMap(users), [users]);
  const colorForUser = useCallback(
    (userId) => userColorMap.get(userId) || "#6b7280",
    [userColorMap]
  );

  const activeUsersInMonth = useMemo(() => {
    const ids = new Set();
    days.forEach((d) => {
      if (d.userId) ids.add(d.userId);
    });
    return Array.from(ids);
  }, [days]);

  // Pedido do usuario 2026-10-13: redesign do calendario - cada pessoa
  // ganha um conjunto {bg,fg,dot} (ver PERSON_COLOR_SETS), atribuido pela
  // ordem de primeira aparicao no mes (mesma ordem de activeUsersInMonth,
  // que e' tambem a ordem exibida na legenda) - igual ao mockup fornecido.
  const schedulePersonColors = useMemo(() => {
    const map = new Map();
    activeUsersInMonth.forEach((id, i) => {
      map.set(id, PERSON_COLOR_SETS[i % PERSON_COLOR_SETS.length]);
    });
    return map;
  }, [activeUsersInMonth]);

  const scheduleDaysCountByUser = useMemo(() => {
    const counts = new Map();
    days.forEach((d) => {
      if (!d.userId) return;
      counts.set(d.userId, (counts.get(d.userId) || 0) + 1);
    });
    return counts;
  }, [days]);

  // Pedido do usuario 2026-10-13: "a impressao do calendario esta
  // diferente do que esta em tela" - a impressao agora reusa o MESMO
  // visual da tela (PrintCalendarGrid abaixo reaproveita as classes
  // schedule*). As cores por pessoa aqui sao calculadas pela ordem de
  // primeira aparicao no LOTE INTEIRO de impressao (todos os meses
  // selecionados, em ordem), nao por mes isolado - assim a mesma pessoa
  // mantem a mesma cor entre os meses impressos juntos.
  const printPersonColors = useMemo(() => {
    const map = new Map();
    let i = 0;
    printData.forEach(({ days: pdays }) => {
      pdays.forEach((d) => {
        if (d.userId && !map.has(d.userId)) {
          map.set(d.userId, PERSON_COLOR_SETS[i % PERSON_COLOR_SETS.length]);
          i++;
        }
      });
    });
    return map;
  }, [printData]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(data || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Pedido do usuario 2026-10-13: "mesmo o Matheus estando configurado
  // como ferias ele deixa eu colocar ele na escala" - o menu de
  // atribuicao manual (aba Escala) precisa saber quem esta de ferias pra
  // bloquear, mas unavailabilities so' era carregado ao abrir a aba
  // Configuracoes. Carrega de cara, independente da aba ativa.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/on-call/unavailabilities");
        setUnavailabilities(data || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const fetchCalendar = useCallback(async () => {
    setLoadingCal(true);
    try {
      const { data } = await api.get("/on-call/calendar", { params: { year, month } });
      setDays(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingCal(false);
    }
  }, [year, month]);

  useEffect(() => {
    if (tab === 0) fetchCalendar();
  }, [tab, fetchCalendar]);

  const fetchReport = useCallback(async () => {
    setLoadingReport(true);
    try {
      const { data } = await api.get("/on-call/report", { params: { year, month } });
      setReport(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingReport(false);
    }
  }, [year, month]);

  useEffect(() => {
    if (tab === 1) fetchReport();
  }, [tab, fetchReport]);

  // Pedido do usuario 2026-10-13: "caso eu descorde do extra possa
  // retirar" - desconsidera um atendimento extra especifico e recarrega
  // o relatorio pra refletir o novo total.
  const handleDismissExtra = async (detail) => {
    try {
      await api.post("/on-call/extra-services/dismiss", {
        ticketId: detail.ticketId,
        userId: detail.userId,
        date: detail.date
      });
      toast.success("Extra removido do relatório");
      fetchReport();
    } catch (err) {
      toastError(err);
    }
  };

  const fetchSettings = useCallback(async () => {
    try {
      const [{ data: ratesData }, { data: holidaysData }, { data: unavailData }] = await Promise.all([
        api.get("/on-call/rates"),
        api.get("/on-call/holidays"),
        api.get("/on-call/unavailabilities"),
      ]);
      setRates(ratesData);
      setHolidays(holidaysData);
      setUnavailabilities(unavailData);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    if (tab === 2) fetchSettings();
  }, [tab, fetchSettings]);

  const goPrevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const goNextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  const handleOpenAssign = (event, day) => {
    if (isViewOnly) return;
    if (day.dayType === "sunday" || day.outOfMonth) return;
    setAssignAnchor(event.currentTarget);
    setAssignDate(day);
  };
  const handleCloseAssign = () => {
    setAssignAnchor(null);
    setAssignDate(null);
  };
  const handlePickAssign = async (userId) => {
    if (!assignDate) return;
    try {
      await api.put("/on-call/assignments", { date: assignDate.date, userId });
      setDays((prev) =>
        prev.map((d) =>
          d.date === assignDate.date
            ? { ...d, userId, userName: userId ? usersById.get(userId)?.name : null }
            : d
        )
      );
    } catch (err) {
      toastError(err);
    }
    handleCloseAssign();
  };

  const handleOpenGenerate = () => {
    setGenStart(`${year}-${pad(month)}-01`);
    setGenEnd(`${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`);
    setGenUserIds([]);
    setGenerateOpen(true);
  };
  const handleGenerate = async () => {
    if (genUserIds.length === 0) {
      toast.error("Selecione ao menos um usuário");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/on-call/generate", {
        startDate: genStart,
        endDate: genEnd,
        userIds: genUserIds,
      });
      toast.success(
        `Escala gerada: ${data.assignedDays} dias atribuídos` +
          (data.forcedRepeats > 0 ? ` (${data.forcedRepeats} repetições forçadas por falta de gente disponível)` : "")
      );
      setGenerateOpen(false);
      fetchCalendar();
    } catch (err) {
      toastError(err);
    } finally {
      setGenerating(false);
    }
  };

  const calendarCells = useMemo(() => {
    if (days.length === 0) return [];
    const firstDow = new Date(`${days[0].date}T12:00:00`).getDay();
    const leading = Array.from({ length: firstDow }, () => null);
    return [...leading, ...days];
  }, [days]);

  const togglePrintMonth = (key) => {
    setPrintMonths((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handlePrintCalendar = async () => {
    if (printMonths.length === 0) return;
    setPrintAnchor(null);
    setPreparingPrint(true);
    try {
      const results = await Promise.all(
        printMonths.map(async (key) => {
          const [y, m] = key.split("-").map(Number);
          if (y === year && m === month) {
            return { year: y, month: m, days };
          }
          const { data } = await api.get("/on-call/calendar", { params: { year: y, month: m } });
          return { year: y, month: m, days: data };
        })
      );
      results.sort((a, b) => (a.year - b.year) || (a.month - b.month));
      setPrintData(results);
      // espera o print-only renderizar antes de chamar o dialogo de impressao
      setTimeout(() => window.print(), 150);
    } catch (err) {
      toastError(err);
    } finally {
      setPreparingPrint(false);
    }
  };

  const handleSaveRates = async () => {
    setSavingRates(true);
    try {
      const { data } = await api.put("/on-call/rates", rates);
      setRates(data);
      toast.success("Valores salvos");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingRates(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!newHolidayDate || !newHolidayName) return;
    try {
      const { data } = await api.post("/on-call/holidays", { date: newHolidayDate, name: newHolidayName });
      setHolidays((prev) => [...prev, data].sort((a, b) => a.date.localeCompare(b.date)));
      setNewHolidayDate("");
      setNewHolidayName("");
    } catch (err) {
      toastError(err);
    }
  };
  const handleDeleteHoliday = async (id) => {
    try {
      await api.delete(`/on-call/holidays/${id}`);
      setHolidays((prev) => prev.filter((h) => h.id !== id));
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddUnavailability = async () => {
    if (!newUnavailUserId || !newUnavailStart || !newUnavailEnd) return;
    try {
      const { data } = await api.post("/on-call/unavailabilities", {
        userId: newUnavailUserId,
        startDate: newUnavailStart,
        endDate: newUnavailEnd,
      });
      const user = usersById.get(Number(newUnavailUserId));
      setUnavailabilities((prev) => [...prev, { ...data, user }]);
      setNewUnavailUserId("");
      setNewUnavailStart("");
      setNewUnavailEnd("");
    } catch (err) {
      toastError(err);
    }
  };
  const handleDeleteUnavailability = async (id) => {
    try {
      await api.delete(`/on-call/unavailabilities/${id}`);
      setUnavailabilities((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      {/* Pedido do usuario 2026-10-13: "preciso que saia colorido na
          impressao" - por padrao o navegador ignora cor de fundo ao
          imprimir a nao ser que seja forcado via print-color-adjust;
          isso precisa valer pra cor inline (colorForUser) em qualquer
          elemento, por isso e' um <style> global com "*" em vez de uma
          classe JSS (JSS nao tem seletor universal facil). */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}</style>
      {/* Pedido do usuario 2026-10-13: redesign do calendario de escala -
          fonte "Plus Jakarta Sans" usada so' nessa pagina, carregada via
          link (nao mexe no index.html global do app). */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap"
      />
      <Box className={classes.noPrint}>
        <MainHeader>
          <Title>Sobreaviso</Title>
        </MainHeader>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Escala" />
          {!isViewOnly && <Tab label="Relatório" />}
          {!isViewOnly && <Tab label="Configurações" />}
        </Tabs>
      </Box>

      {tab === 0 && (
        <div className={`${classes.mainPaper} ${classes.noPrint}`}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
            <Box className={classes.monthNav}>
              <IconButton size="small" onClick={goPrevMonth}><ChevronLeftIcon /></IconButton>
              <IconButton size="small" onClick={goNextMonth}><ChevronRightIcon /></IconButton>
            </Box>
            <Box display="flex" style={{ gap: 8 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={(e) => { setPrintAnchor(e.currentTarget); setPrintMonths([`${year}-${pad(month)}`]); }}
              >
                Imprimir calendário
              </Button>
              {!isViewOnly && (
                <Button size="small" variant="contained" color="primary" startIcon={<FlashOnIcon />} onClick={handleOpenGenerate}>
                  Gerar escala (período)
                </Button>
              )}
            </Box>
          </Box>

          <Menu anchorEl={printAnchor} open={Boolean(printAnchor)} onClose={() => setPrintAnchor(null)}>
            <Box style={{ padding: 10, minWidth: 200 }}>
              <Typography variant="caption" color="textSecondary" style={{ textTransform: "uppercase", fontWeight: 700 }}>
                Selecionar meses
              </Typography>
              {Array.from({ length: 6 }, (_, i) => {
                const d = new Date(year, month - 1 + i, 1);
                const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
                return (
                  <FormControlLabel
                    key={key}
                    style={{ display: "flex" }}
                    control={
                      <Checkbox
                        size="small"
                        checked={printMonths.includes(key)}
                        onChange={() => togglePrintMonth(key)}
                      />
                    }
                    label={`${MONTH_NAMES[d.getMonth()]}/${d.getFullYear()}`}
                  />
                );
              })}
              <Button fullWidth size="small" variant="contained" color="primary" style={{ marginTop: 8 }} onClick={handlePrintCalendar} disabled={preparingPrint}>
                {preparingPrint ? <CircularProgress size={16} /> : `Gerar impressão (${printMonths.length} ${printMonths.length === 1 ? "mês" : "meses"})`}
              </Button>
            </Box>
          </Menu>

          <div className={classes.scheduleWrap}>
            <div className={classes.scheduleHeader}>
              <div>
                <p className={classes.scheduleEyebrow}>Escala de plantão</p>
                <h1 className={classes.scheduleTitle}>
                  {MONTH_NAMES[month - 1]} <span className={classes.scheduleTitleYear}>{year}</span>
                </h1>
              </div>
              <div className={classes.scheduleLegend}>
                {activeUsersInMonth.map((userId) => {
                  const palette = schedulePersonColors.get(userId) || PERSON_COLOR_SETS[0];
                  const name = usersById.get(userId)?.name || "?";
                  const count = scheduleDaysCountByUser.get(userId) || 0;
                  return (
                    <div key={userId} className={classes.scheduleChip} style={{ background: palette.bg }}>
                      <span className={classes.scheduleChipAvatar} style={{ background: palette.dot }}>
                        {getInitials(name)}
                      </span>
                      <div>
                        <div className={classes.scheduleChipName} style={{ color: palette.fg }}>{name}</div>
                        <div className={classes.scheduleChipCount} style={{ color: palette.fg }}>
                          {count} {count === 1 ? "dia" : "dias"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {loadingCal ? (
              <Box display="flex" justifyContent="center" padding={4}><CircularProgress size={24} /></Box>
            ) : (
              <>
                <div className={classes.scheduleDowRow}>
                  {DOW_LABELS.map((l) => <div key={l} className={classes.scheduleDowLabel}>{l}</div>)}
                </div>
                <div className={classes.scheduleGrid}>
                  {calendarCells.map((day, idx) => {
                    if (!day) return <div key={`empty-${idx}`} className={classes.scheduleCellEmpty} />;
                    const isSunday = day.dayType === "sunday";
                    const isHoliday = day.dayType === "holiday";
                    const palette = day.userId ? schedulePersonColors.get(day.userId) : null;
                    const hasPerson = Boolean(day.userId);

                    let cellStyle;
                    if (isHoliday) {
                      cellStyle = { background: "#FFFFFF", boxShadow: "inset 0 0 0 2px #C0262D" };
                    } else if (palette) {
                      cellStyle = { background: palette.bg };
                    } else {
                      cellStyle = {};
                    }
                    if (isSunday || isViewOnly) cellStyle.cursor = "default";

                    return (
                      <div
                        key={day.date}
                        className={`${classes.scheduleCell} ${!isHoliday && !palette ? classes.scheduleCellOff : ""}`}
                        style={cellStyle}
                        onClick={(e) => handleOpenAssign(e, day)}
                      >
                        {isHoliday && <span className={classes.scheduleHolidayBadge}>FERIADO</span>}
                        <span
                          className={classes.scheduleDayNum}
                          style={{ color: isHoliday ? "#1C1C1E" : palette ? palette.fg : "#9A9AA0" }}
                        >
                          {Number(day.date.slice(-2))}
                        </span>
                        {isHoliday && day.holidayName && (
                          <span className={classes.scheduleHolidayName}>{day.holidayName}</span>
                        )}
                        <div className={classes.scheduleBottomRow}>
                          {hasPerson ? (
                            <>
                              <span
                                className={classes.scheduleDot}
                                style={{ background: (schedulePersonColors.get(day.userId) || {}).dot || "#9A9AA0" }}
                              />
                              <span
                                className={classes.schedulePersonName}
                                style={{ color: isHoliday ? "#1C1C1E" : (palette ? palette.fg : "#1C1C1E") }}
                              >
                                {day.userName}
                              </span>
                            </>
                          ) : (
                            isSunday && <span className={classes.scheduleOffLabel}>Folga</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <Menu anchorEl={assignAnchor} open={Boolean(assignAnchor)} onClose={handleCloseAssign}>
            <MenuItem onClick={() => handlePickAssign(null)}>
              <em>Ninguém (limpar)</em>
            </MenuItem>
            {users.map((u) => {
              // Pedido do usuario 2026-10-13: "mesmo o Matheus estando
              // configurado como ferias ele deixa eu colocar ele na
              // escala" - bloqueia quem tem indisponibilidade cadastrada
              // cobrindo a data clicada (mesma checagem que o gerador
              // automatico ja fazia, so' que aqui no manual tambem).
              const unavail = assignDate && unavailabilities.find(
                (un) => un.userId === u.id && assignDate.date >= un.startDate && assignDate.date <= un.endDate
              );
              return (
                <MenuItem
                  key={u.id}
                  selected={assignDate?.userId === u.id}
                  disabled={Boolean(unavail)}
                  onClick={() => handlePickAssign(u.id)}
                >
                  <span className={classes.legendDot} style={{ background: colorForUser(u.id), marginRight: 8, display: "inline-block" }} />
                  {u.name}
                  {unavail && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: theme.palette.text.secondary }}>
                      ({unavail.reason || "indisponível"})
                    </span>
                  )}
                </MenuItem>
              );
            })}
          </Menu>

          <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Gerar escala automática</DialogTitle>
            <DialogContent>
              <Box display="flex" style={{ gap: 8 }}>
                <TextField
                  label="De"
                  type="date"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={genStart}
                  onChange={(e) => setGenStart(e.target.value)}
                />
                <TextField
                  label="Até"
                  type="date"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={genEnd}
                  onChange={(e) => setGenEnd(e.target.value)}
                />
              </Box>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12, marginBottom: 4 }}>
                Usuários elegíveis
              </Typography>
              {users.map((u) => (
                <FormControlLabel
                  key={u.id}
                  style={{ display: "flex" }}
                  control={
                    <Checkbox
                      size="small"
                      checked={genUserIds.includes(u.id)}
                      onChange={() =>
                        setGenUserIds((prev) =>
                          prev.includes(u.id) ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                        )
                      }
                    />
                  }
                  label={u.name}
                />
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setGenerateOpen(false)}>Cancelar</Button>
              <Button onClick={handleGenerate} color="primary" variant="contained" disabled={generating}>
                {generating ? <CircularProgress size={18} /> : "Gerar"}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}

      {tab === 1 && (
        <OnCallReportTab
          classes={classes}
          theme={theme}
          year={year}
          month={month}
          setYear={setYear}
          setMonth={setMonth}
          report={report}
          loading={loadingReport}
          onBeforePrint={() => setPrintData([])}
          onDismissExtra={handleDismissExtra}
        />
      )}

      {tab === 2 && (
        <OnCallSettingsTab
          classes={classes}
          rates={rates}
          setRates={setRates}
          savingRates={savingRates}
          onSaveRates={handleSaveRates}
          holidays={holidays}
          newHolidayDate={newHolidayDate}
          setNewHolidayDate={setNewHolidayDate}
          newHolidayName={newHolidayName}
          setNewHolidayName={setNewHolidayName}
          onAddHoliday={handleAddHoliday}
          onDeleteHoliday={handleDeleteHoliday}
          unavailabilities={unavailabilities}
          users={users}
          newUnavailUserId={newUnavailUserId}
          setNewUnavailUserId={setNewUnavailUserId}
          newUnavailStart={newUnavailStart}
          setNewUnavailStart={setNewUnavailStart}
          newUnavailEnd={newUnavailEnd}
          setNewUnavailEnd={setNewUnavailEnd}
          onAddUnavailability={handleAddUnavailability}
          onDeleteUnavailability={handleDeleteUnavailability}
        />
      )}

      {/* Pedido do usuario 2026-10-09: imprimir mais de um mes de uma vez
          - fica escondido na tela (classes.printOnly), so aparece via
          @media print quando o resto (classes.noPrint, aplicado no
          proprio MainContainer via wrapper) some. */}
      {printData.length > 0 && (
        <Box className={classes.printOnly}>
          {printData.map(({ year: py, month: pm, days: pdays }) => (
            <div key={`${py}-${pm}`} className={classes.printMonthBlock}>
              <PrintCalendarGrid year={py} month={pm} days={pdays} personColors={printPersonColors} usersById={usersById} />
            </div>
          ))}
        </Box>
      )}
    </MainContainer>
  );
};

// Pedido do usuario 2026-10-13: "a impressao do calendario esta
// diferente do que esta em tela" - reaproveita exatamente as mesmas
// classes schedule* usadas na aba Escala (em vez de uma tabela HTML a
// parte com seu proprio estilo), pra impressao e tela ficarem identicas.
const PrintCalendarGrid = ({ year, month, days, personColors, usersById }) => {
  const classes = useStyles();
  if (days.length === 0) return null;
  const firstDow = new Date(`${days[0].date}T12:00:00`).getDay();
  const leading = Array.from({ length: firstDow }, () => null);
  const cells = [...leading, ...days];

  const activeUserIds = [];
  const countByUser = new Map();
  days.forEach((d) => {
    if (!d.userId) return;
    if (!countByUser.has(d.userId)) activeUserIds.push(d.userId);
    countByUser.set(d.userId, (countByUser.get(d.userId) || 0) + 1);
  });

  return (
    <div className={classes.scheduleWrap}>
      <div className={classes.scheduleHeader}>
        <div>
          <p className={classes.scheduleEyebrow}>Escala de plantão</p>
          <h1 className={classes.scheduleTitle}>
            {MONTH_NAMES[month - 1]} <span className={classes.scheduleTitleYear}>{year}</span>
          </h1>
        </div>
        <div className={classes.scheduleLegend}>
          {activeUserIds.map((userId) => {
            const palette = personColors.get(userId) || PERSON_COLOR_SETS[0];
            const name = usersById.get(userId)?.name || "?";
            const count = countByUser.get(userId) || 0;
            return (
              <div key={userId} className={classes.scheduleChip} style={{ background: palette.bg }}>
                <span className={classes.scheduleChipAvatar} style={{ background: palette.dot }}>
                  {getInitials(name)}
                </span>
                <div>
                  <div className={classes.scheduleChipName} style={{ color: palette.fg }}>{name}</div>
                  <div className={classes.scheduleChipCount} style={{ color: palette.fg }}>
                    {count} {count === 1 ? "dia" : "dias"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={classes.scheduleDowRow}>
        {DOW_LABELS.map((l) => <div key={l} className={classes.scheduleDowLabel}>{l}</div>)}
      </div>
      <div className={classes.scheduleGrid}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} className={classes.scheduleCellEmpty} />;
          const isSunday = day.dayType === "sunday";
          const isHoliday = day.dayType === "holiday";
          const palette = day.userId ? personColors.get(day.userId) : null;
          const hasPerson = Boolean(day.userId);

          let cellStyle;
          if (isHoliday) {
            cellStyle = { background: "#FFFFFF", boxShadow: "inset 0 0 0 2px #C0262D" };
          } else if (palette) {
            cellStyle = { background: palette.bg };
          } else {
            cellStyle = {};
          }

          return (
            <div
              key={day.date}
              className={`${classes.scheduleCell} ${!isHoliday && !palette ? classes.scheduleCellOff : ""}`}
              style={cellStyle}
            >
              {isHoliday && <span className={classes.scheduleHolidayBadge}>FERIADO</span>}
              <span
                className={classes.scheduleDayNum}
                style={{ color: isHoliday ? "#1C1C1E" : palette ? palette.fg : "#9A9AA0" }}
              >
                {Number(day.date.slice(-2))}
              </span>
              {isHoliday && day.holidayName && (
                <span className={classes.scheduleHolidayName}>{day.holidayName}</span>
              )}
              <div className={classes.scheduleBottomRow}>
                {hasPerson ? (
                  <>
                    <span
                      className={classes.scheduleDot}
                      style={{ background: (personColors.get(day.userId) || {}).dot || "#9A9AA0" }}
                    />
                    <span
                      className={classes.schedulePersonName}
                      style={{ color: isHoliday ? "#1C1C1E" : (palette ? palette.fg : "#1C1C1E") }}
                    >
                      {day.userName}
                    </span>
                  </>
                ) : (
                  isSunday && <span className={classes.scheduleOffLabel}>Folga</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OnCallReportTab = ({ classes, theme, year, month, setYear, setMonth, report, loading, onBeforePrint, onDismissExtra }) => {
  const history = useHistory();
  const [expandedExtraUserId, setExpandedExtraUserId] = useState(null);
  const goPrevMonth = () => { if (month === 1) { setMonth(12); setYear((y) => y - 1); } else setMonth((m) => m - 1); };
  const goNextMonth = () => { if (month === 12) { setMonth(1); setYear((y) => y + 1); } else setMonth((m) => m + 1); };

  if (loading || !report) {
    return <Box display="flex" justifyContent="center" padding={4}><CircularProgress size={24} /></Box>;
  }

  return (
    <div className={classes.mainPaper}>
      <Box display="flex" alignItems="center" justifyContent="space-between" className={classes.noPrint} style={{ marginBottom: 12 }}>
        <Box className={classes.monthNav}>
          <IconButton size="small" onClick={goPrevMonth}><ChevronLeftIcon /></IconButton>
          <Typography variant="subtitle1" style={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {MONTH_NAMES[month - 1]} {year}
          </Typography>
          <IconButton size="small" onClick={goNextMonth}><ChevronRightIcon /></IconButton>
        </Box>
        <Button size="small" variant="contained" style={{ background: "#1f8a5f", color: "#fff" }} startIcon={<PrintIcon />} onClick={() => { onBeforePrint(); window.print(); }}>
          Imprimir
        </Button>
      </Box>

      <Typography align="center" style={{ fontWeight: 800, fontSize: 13, letterSpacing: ".02em" }}>
        SOBREAVISO / PLANTÃO — {MONTH_NAMES[month - 1].toUpperCase()} {year}
      </Typography>
      <Typography align="center" variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
        Gerado em {new Date().toLocaleDateString("pt-BR")}
      </Typography>

      <table className={classes.reportTable}>
        <thead className={classes.reportHeadRow} style={{ background: "#4a3fd6" }}>
          <tr><th colSpan={4}>SOBREAVISO (semana)</th></tr>
          <tr><th>De</th><th>Até</th><th>Técnico</th><th style={{ textAlign: "right" }}>Valor</th></tr>
        </thead>
        <tbody>
          {report.weekdayRows.map((r, i) => (
            <tr key={i}>
              <td className={classes.reportTd}>{formatBR(r.from)}</td>
              <td className={classes.reportTd}>{formatBR(r.to)}</td>
              <td className={classes.reportTd}>{r.userName}</td>
              <td className={`${classes.reportTd} ${classes.reportValTd}`}>{formatCurrency(r.value)}</td>
            </tr>
          ))}
          {report.weekdayRows.length === 0 && <tr><td className={classes.reportTd} colSpan={4}><em>Nenhum registro</em></td></tr>}
        </tbody>
      </table>
      <Box height={10} />

      <table className={classes.reportTable}>
        <thead className={classes.reportHeadRow} style={{ background: "#7a5cff" }}>
          <tr><th colSpan={4}>PLANTÃO (sábado)</th></tr>
          <tr><th>Data</th><th></th><th>Técnico</th><th style={{ textAlign: "right" }}>Valor</th></tr>
        </thead>
        <tbody>
          {report.saturdayRows.map((r, i) => (
            <tr key={i}>
              <td className={classes.reportTd}>{formatBR(r.from)}</td>
              <td className={classes.reportTd}></td>
              <td className={classes.reportTd}>{r.userName}</td>
              <td className={`${classes.reportTd} ${classes.reportValTd}`}>{formatCurrency(r.value)}</td>
            </tr>
          ))}
          {report.saturdayRows.length === 0 && <tr><td className={classes.reportTd} colSpan={4}><em>Nenhum registro</em></td></tr>}
        </tbody>
      </table>
      <Box height={10} />

      <table className={classes.reportTable}>
        <thead className={classes.reportHeadRow} style={{ background: "#d97a2e" }}>
          <tr><th colSpan={4}>FERIADO</th></tr>
          <tr><th>Data</th><th>Nome</th><th>Técnico</th><th style={{ textAlign: "right" }}>Valor</th></tr>
        </thead>
        <tbody>
          {report.holidayRows.map((r, i) => (
            <tr key={i}>
              <td className={classes.reportTd}>{formatBR(r.from)}</td>
              <td className={classes.reportTd}>{r.holidayName}</td>
              <td className={classes.reportTd}>{r.userName}</td>
              <td className={`${classes.reportTd} ${classes.reportValTd}`}>{formatCurrency(r.value)}</td>
            </tr>
          ))}
          {report.holidayRows.length === 0 && <tr><td className={classes.reportTd} colSpan={4}><em>Nenhum registro</em></td></tr>}
        </tbody>
      </table>
      <Box height={10} />

      <table className={classes.reportTable}>
        <thead className={classes.reportHeadRow} style={{ background: "#7a5cff" }}>
          <tr><th colSpan={4}>
            ATENDIMENTOS EXTRAS · bônus por atendimento
            <span style={{ fontWeight: 400, opacity: .85 }}> (domingo · dia útil após 22h · sáb/feriado após 17:30)</span>
          </th></tr>
          <tr><th>Técnico</th><th>Qtd.</th><th></th><th style={{ textAlign: "right" }}>Valor</th></tr>
        </thead>
        <tbody>
          {report.extraRows.map((r, i) => {
            const isOpen = expandedExtraUserId === r.userId;
            const details = (report.extraServiceDetails || []).filter((d) => d.userId === r.userId);
            return (
              <React.Fragment key={i}>
                <tr
                  className={classes.noPrint}
                  style={{ cursor: "pointer" }}
                  onClick={() => setExpandedExtraUserId(isOpen ? null : r.userId)}
                >
                  <td className={classes.reportTd}>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      {r.userName}
                    </Box>
                  </td>
                  <td className={classes.reportTd}>{r.count}</td>
                  <td className={classes.reportTd}></td>
                  <td className={`${classes.reportTd} ${classes.reportValTd}`}>{formatCurrency(r.value)}</td>
                </tr>
                {/* versao so' de impressao: igual a linha de cima, sem o
                    toggle (impresso sempre mostra fechado, sem clique) */}
                <tr className={classes.printOnly}>
                  <td className={classes.reportTd}>{r.userName}</td>
                  <td className={classes.reportTd}>{r.count}</td>
                  <td className={classes.reportTd}></td>
                  <td className={`${classes.reportTd} ${classes.reportValTd}`}>{formatCurrency(r.value)}</td>
                </tr>
                {isOpen && (
                  <tr className={classes.noPrint}>
                    <td colSpan={4} style={{ padding: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <Box style={{ background: theme.palette.action.hover, padding: "6px 10px 10px 30px" }}>
                        {details.length === 0 && <Typography variant="caption" color="textSecondary">Nenhum detalhe encontrado</Typography>}
                        {details.map((d, di) => (
                          <Box
                            key={di}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            style={{ padding: "6px 0", borderBottom: di < details.length - 1 ? `1px dashed ${theme.palette.divider}` : "none", gap: 8 }}
                          >
                            <Box style={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" style={{ fontWeight: 700 }}>
                                {formatBR(d.date)} — Ticket #{d.ticketId} — {d.contactName}
                                {d.organizationName && <span style={{ fontWeight: 400, color: theme.palette.text.secondary }}> ({d.organizationName})</span>}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {d.queueName ? `${d.queueName} · ` : ""}{d.lastMessage || "(sem resumo de mensagem)"}
                              </Typography>
                            </Box>
                            <Box display="flex" style={{ gap: 4, flexShrink: 0 }}>
                              <IconButton size="small" title="Abrir ticket" onClick={() => history.push(`/tickets/${d.ticketUuid}`)}>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                title="Remover este extra (discordo da cobrança)"
                                onClick={() => {
                                  if (window.confirm("Remover este atendimento extra do relatório? Essa ação não pode ser desfeita.")) {
                                    onDismissExtra(d);
                                  }
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {report.extraRows.length === 0 && <tr><td className={classes.reportTd} colSpan={4}><em>Nenhum registro</em></td></tr>}
        </tbody>
      </table>
      <Box height={10} />

      <table className={classes.reportTable}>
        <thead className={classes.reportHeadRow} style={{ background: "#20222b" }}>
          <tr><th colSpan={4}>TOTAIS DO MÊS</th></tr>
        </thead>
        <tbody>
          {report.totals.map((t) => (
            <tr key={t.userId}>
              <td className={classes.reportTd} colSpan={3} style={{ fontWeight: 700 }}>
                {t.userName}{" "}
                <span style={{ fontWeight: 400, color: theme.palette.text.secondary }}>
                  ({t.holidayDays > 0 && `${t.holidayDays} feriado${t.holidayDays > 1 ? "s" : ""} + `}
                  {t.weekdayDays} dias + {t.saturdayDays} sáb
                  {t.extraServices > 0 && ` + ${t.extraServices} extras`})
                </span>
              </td>
              <td className={`${classes.reportTd} ${classes.reportValTd}`} style={{ fontWeight: 700 }}>
                {formatCurrency(t.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OnCallSettingsTab = ({
  classes, rates, setRates, savingRates, onSaveRates,
  holidays, newHolidayDate, setNewHolidayDate, newHolidayName, setNewHolidayName, onAddHoliday, onDeleteHoliday,
  unavailabilities, users, newUnavailUserId, setNewUnavailUserId, newUnavailStart, setNewUnavailStart,
  newUnavailEnd, setNewUnavailEnd, onAddUnavailability, onDeleteUnavailability
}) => {
  if (!rates) return <Box display="flex" justifyContent="center" padding={4}><CircularProgress size={24} /></Box>;

  const rateField = (label, key, note) => (
    <Paper variant="outlined" className={classes.rateCard}>
      <Typography variant="caption" style={{ textTransform: "uppercase", fontWeight: 700, color: "#767b87" }}>
        {label}
      </Typography>
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        type="number"
        value={rates[key]}
        onChange={(e) => setRates({ ...rates, [key]: e.target.value })}
        InputProps={{ startAdornment: <Box marginRight={0.5} color="textSecondary">R$</Box> }}
      />
      <Typography variant="caption" color="textSecondary">{note}</Typography>
    </Paper>
  );

  return (
    <div className={`${classes.mainPaper} ${classes.noPrint}`}>
      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gridGap={14} maxWidth={820}>
        {rateField("Dia de semana", "weekdayRate", "seg–sex, fora de feriado · /dia")}
        {rateField("Sábado", "saturdayRate", "/dia")}
        {rateField("Feriado", "holidayRate", "qualquer dia da semana · /dia")}
        {rateField("Atendimento extra", "extraServiceRate", "domingo · dia útil após 22h · sáb/fer após 17:30")}
      </Box>
      <Button
        variant="contained"
        color="primary"
        style={{ marginTop: 16 }}
        onClick={onSaveRates}
        disabled={savingRates}
      >
        {savingRates ? <CircularProgress size={18} /> : "Salvar valores"}
      </Button>

      <Box marginTop={4} paddingTop={3} borderTop="1px solid #ebecf1">
        <Typography variant="subtitle2" gutterBottom>Feriados extras (regionais)</Typography>
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10 }}>
          Feriados nacionais já entram automaticamente. Use isso só pra feriados municipais/regionais.
        </Typography>
        {holidays.map((h) => (
          <Box key={h.id} display="flex" alignItems="center" justifyContent="space-between" maxWidth={420} paddingY={0.5} borderBottom="1px solid #f1f1f4">
            <Typography variant="body2">{formatBR(h.date)}/{h.date.slice(0, 4)} — {h.name}</Typography>
            <IconButton size="small" onClick={() => onDeleteHoliday(h.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Box>
        ))}
        <Box display="flex" style={{ gap: 8 }} marginTop={1} maxWidth={480}>
          <TextField type="date" size="small" variant="outlined" value={newHolidayDate} onChange={(e) => setNewHolidayDate(e.target.value)} />
          <TextField size="small" variant="outlined" placeholder="Nome do feriado" value={newHolidayName} onChange={(e) => setNewHolidayName(e.target.value)} />
          <IconButton size="small" color="primary" onClick={onAddHoliday}><AddIcon /></IconButton>
        </Box>
      </Box>

      <Box marginTop={4} paddingTop={3} borderTop="1px solid #ebecf1">
        <Typography variant="subtitle2" gutterBottom>Indisponibilidades (férias)</Typography>
        {unavailabilities.map((u) => (
          <Box key={u.id} display="flex" alignItems="center" justifyContent="space-between" maxWidth={420} paddingY={0.5} borderBottom="1px solid #f1f1f4">
            <Typography variant="body2">{u.user?.name} — {formatBR(u.startDate)} a {formatBR(u.endDate)}</Typography>
            <IconButton size="small" onClick={() => onDeleteUnavailability(u.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Box>
        ))}
        <Box display="flex" style={{ gap: 8 }} marginTop={1} maxWidth={560} alignItems="center">
          <TextField select size="small" variant="outlined" style={{ minWidth: 140 }} value={newUnavailUserId} onChange={(e) => setNewUnavailUserId(e.target.value)}>
            <MenuItem value=""><em>Usuário</em></MenuItem>
            {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
          </TextField>
          <TextField type="date" size="small" variant="outlined" value={newUnavailStart} onChange={(e) => setNewUnavailStart(e.target.value)} />
          <TextField type="date" size="small" variant="outlined" value={newUnavailEnd} onChange={(e) => setNewUnavailEnd(e.target.value)} />
          <IconButton size="small" color="primary" onClick={onAddUnavailability}><AddIcon /></IconButton>
        </Box>
      </Box>
    </div>
  );
};

export default OnCall;
